import {
  Card,
  EmailInput,
  PhoneInput,
  PhoneNumber as PlatformPhoneNumber,
  TextInput,
} from 'platform/components';
import {Grid, GridItem, Heading, Show, Text, VStack} from 'platform/foundation';

import {Dispatch, SetStateAction} from 'react';

import {GetSignableDocumentListResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {CustomerSelectSearch, getSaneCustomerContactDetails, usePhoneNumbers} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DefaultSignatoriesFormData} from '../../../types/DefaultSignatoriesFormData';
import {DefaultSignatoryFieldErrors} from '../../../utils/getDefaultSignatoriesErrors';
import {UserSelectChoice} from './UserSelectChoice';

interface DefaultSignatoriesProps extends TestIdProps {
  signatories: GetSignableDocumentListResponseBody['metadata'];
  formData: DefaultSignatoriesFormData;
  onFormDataChange: Dispatch<SetStateAction<DefaultSignatoriesFormData>>;
  errors: Record<string, DefaultSignatoryFieldErrors>;
}

export function DefaultSignatories(props: DefaultSignatoriesProps) {
  const {countriesOptions} = usePhoneNumbers();

  return (
    <VStack spacing={4}>
      <VStack spacing={2}>
        <Heading size={3}>{i18n.t('entity.document.labels.defaultValuesForAllDocuments')}</Heading>

        <Text size="small" color="secondary">
          {i18n.t('entity.document.labels.defaultValuesForAllDocumentsDescription')}
        </Text>
      </VStack>

      <VStack spacing={4}>
        {props.signatories.map((signatory) => (
          <Card variant="inlineGrey" title={signatory.title} key={signatory.signatoryPerson}>
            <Grid columns={3}>
              <GridItem span={3}>
                <Show when={signatory.signatoryPerson === 'customer'}>
                  <CustomerSelectSearch
                    defaultSearchString={props.formData[signatory.signatoryPerson]?.name}
                    onCustomerSelect={(data) => {
                      const {name, email, phoneNumber, prefix} =
                        getSaneCustomerContactDetails(data);

                      props.onFormDataChange((prevState) => ({
                        ...prevState,
                        [signatory.signatoryPerson]: {
                          email,
                          name,
                          id: data.id,
                          phoneNumber: {number: phoneNumber, prefix, countryCode: data.countryCode},
                        },
                      }));
                    }}
                    data-testid={suffixTestId('CustomerSelectChoice', props)}
                  />
                </Show>

                <Show when={signatory.signatoryPerson === 'tenant'}>
                  <UserSelectChoice
                    defaultUserId={props.formData[signatory.signatoryPerson]?.id}
                    onSelect={(userValues) => {
                      props.onFormDataChange((prevState) => ({
                        ...prevState,
                        [signatory.signatoryPerson]: {
                          email: userValues.email,
                          name: userValues.name,
                          id: userValues.id,
                          phoneNumber: userValues.phoneNumber,
                        },
                      }));
                    }}
                    data-testid={suffixTestId('UserSelectChoice', props)}
                  />
                </Show>
              </GridItem>

              <TextInput
                value={props.formData[signatory.signatoryPerson]?.name}
                label={i18n.t('entity.person.labels.firstName')}
                isRequired
                errorMessage={props.errors[signatory.signatoryPerson]?.name}
                onChange={(name) =>
                  props.onFormDataChange((prevState) => ({
                    ...prevState,
                    [signatory.signatoryPerson]: {...prevState[signatory.signatoryPerson], name},
                  }))
                }
                data-testid={suffixTestId('defaultName', props)}
              />

              <EmailInput
                value={props.formData[signatory.signatoryPerson]?.email}
                label={i18n.t('general.labels.email')}
                isRequired
                errorMessage={props.errors[signatory.signatoryPerson]?.email}
                onChange={(email) =>
                  props.onFormDataChange((prevState) => ({
                    ...prevState,
                    [signatory.signatoryPerson]: {...prevState[signatory.signatoryPerson], email},
                  }))
                }
                data-testid={suffixTestId('defaultEmail', props)}
              />

              <PhoneInput
                value={
                  props.formData[signatory.signatoryPerson]?.phoneNumber as PlatformPhoneNumber
                }
                label={i18n.t('general.labels.phone')}
                isRequired
                errorMessage={props.errors[signatory.signatoryPerson]?.phoneNumber}
                isNational
                countries={countriesOptions}
                onChange={(phoneNumber) =>
                  props.onFormDataChange((prevState) => ({
                    ...prevState,
                    [signatory.signatoryPerson]: {
                      ...prevState[signatory.signatoryPerson],
                      phoneNumber,
                    },
                  }))
                }
                data-testid={suffixTestId('defaultPhoneNumber', props)}
              />
            </Grid>
          </Card>
        ))}
      </VStack>
    </VStack>
  );
}
